import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Briefcase, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { db } from '../../../firebase';
import { Checkbox } from '../../../components/ui/checkbox';
import { Button } from '../../../components/ui/button';
import { useCompany } from '../../../context/CompanyContext';
import { useRole } from '../../../context/RoleContext';
import { useAuth } from '../../../context/AuthContext';
import { SERVICE_CATALOG, ALL_SERVICE_IDS } from '../../../constants/serviceCatalog';

/**
 * "Services Offered" config. Lives inside Settings → App Configuration.
 *
 * Firestore doc: /companies/{cid}/settings/activeServices
 * Shape: { services: { [id]: boolean }, updatedAt, updatedBy }
 *
 * Default-ON semantics: any missing key renders as enabled.
 * Save guard: at least one service must remain enabled (toast + block).
 */
const ServicesOfferedConfig = () => {
  const { companyId } = useCompany();
  const { isAdmin }   = useRole();
  const { user }      = useAuth();

  const [enabled, setEnabled] = useState(() =>
    Object.fromEntries(ALL_SERVICE_IDS.map((id) => [id, true]))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId, 'settings', 'activeServices'));
        if (cancelled) return;
        const servicesMap = snap.exists() ? (snap.data()?.services || {}) : {};
        // Default-ON: missing key = enabled.
        const next = Object.fromEntries(
          ALL_SERVICE_IDS.map((id) => [id, servicesMap[id] !== false])
        );
        setEnabled(next);
      } catch (err) {
        console.warn('[ServicesOfferedConfig] load error:', err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const toggle = (id) => {
    if (!isAdmin) return;
    setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));
    setDirty(true);
  };

  const enabledCount = Object.values(enabled).filter(Boolean).length;

  const handleSave = async () => {
    if (!companyId || !isAdmin) return;
    if (enabledCount === 0) {
      toast.error('At least one service must remain enabled.');
      return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'companies', companyId, 'settings', 'activeServices'),
        {
          services:  enabled,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        },
        { merge: true },
      );
      toast.success('Services Offered saved');
      setDirty(false);
    } catch (err) {
      console.error('[ServicesOfferedConfig] save error:', err);
      toast.error('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="services-offered-section">
      <div className="flex items-center gap-2 mb-4">
        <Briefcase className="w-5 h-5 text-gray-500" />
        <h3 className="text-base font-semibold text-gray-900">Services This Business Offers</h3>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5">
        <p className="text-sm text-gray-500">
          Services turned off here will be hidden from the Calculator.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SERVICE_CATALOG.map((svc) => {
              const Icon = svc.icon;
              const checked = enabled[svc.id] !== false;
              return (
                <label
                  key={svc.id}
                  className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer select-none ${
                    checked ? 'border-secondary/40 bg-secondary/5' : 'border-gray-200 bg-white'
                  } ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(svc.id)}
                    disabled={!isAdmin}
                    data-testid={`services-offered-${svc.id}`}
                  />
                  <Icon className={`w-4 h-4 ${checked ? 'text-secondary' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium text-gray-800">{svc.label}</span>
                </label>
              );
            })}
          </div>
        )}

        {enabledCount === 0 && !loading && (
          <p
            className="text-xs text-red-600"
            data-testid="services-offered-zero-warning"
          >
            At least one service must remain enabled.
          </p>
        )}

        {isAdmin && (
          <div className="flex items-center justify-end gap-3">
            <span className="text-xs text-gray-400">
              {enabledCount} of {ALL_SERVICE_IDS.length} enabled
            </span>
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || enabledCount === 0}
              data-testid="services-offered-save"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save</>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesOfferedConfig;
